<template>
  <AnswerPage :page="page" />
</template>

<script setup lang="ts">
const page = {
  slug: '/why-workspace369',
  metaTitle: 'Why Choose Workspace369? | Replace Your Client Work Stack',
  description:
    'See why teams choose Workspace369 to replace disconnected CRM, project, inbox, voice, SMS, invoicing, payment, AI, file, and reporting tools.',
  eyebrow: 'Why Workspace369',
  h1: 'Why teams choose Workspace369',
  lede:
    'Teams choose Workspace369 to run CRM, projects, inbox, invoices, payments, files, AI, and reporting from one client record instead of separate tools, from $29 a month on Cadet. It is built for businesses tired of juggling CRMs, project boards, inboxes, phone tools, billing apps, files, spreadsheets, and AI tabs.',
  aiAnswer:
    'Choose Workspace369 when the business needs CRM, projects, inbox, SMS, voice, voicemail, invoices, payments, files, AI, automations, accounting, inventory, and reporting to work from one connected client record. Texts and calls start on Voyager ($74/mo, 2 seats); accounting, inventory, online booking, expenses, and payment plans start on Specialist ($149/mo, 10 seats).',
  facts: [
    { label: 'Main reason to switch', value: 'Replace tool sprawl with one client-work operating system' },
    { label: 'Best-fit teams', value: 'Agencies, consultants, professional-services firms, and service businesses, from one-person studios to multi-team operations' },
    { label: 'Value driver', value: 'Less duplicate work, fewer subscriptions, stronger client context, and cleaner reporting' },
  ],
  summaryKicker: 'Operating advantage',
  summaryTitle: 'Workspace369 wins when the work, money, and communication need to stay together.',
  summary: [
    {
      kicker: 'One client record',
      title: 'No more rebuilding context in every tool.',
      copy:
        'Client profiles, notes, requests, files, projects, inbox messages, SMS, voice, invoices, payments, and reports can live around the same relationship.',
    },
    {
      kicker: 'One money loop',
      title: 'Proposals, invoices, and payments connect to the work that created them.',
      copy:
        'Teams can move from proposal to invoice to payment while keeping delivery, communication, expenses, and follow-up visible.',
    },
    {
      kicker: 'One AI layer',
      title: 'AI is more useful when it has business context.',
      copy:
        'AI compose, rewrite, translate, call summaries, voicemail transcription, search, automations, and reporting become stronger when they operate near client history.',
    },
  ],
  workflowKicker: 'Switching logic',
  workflowTitle: 'When Workspace369 becomes the obvious choice.',
  workflow: [
    { title: 'Stack cost rises', copy: 'CRM, projects, phone, SMS, invoices, files, AI, automation, and reporting each become another renewal.' },
    { title: 'Context breaks', copy: 'Client questions require searching inboxes, spreadsheets, task boards, calendars, files, and billing tools.' },
    { title: 'Reporting slows', copy: 'Revenue, expenses, receivables, client activity, and delivery health need exports before they become useful.' },
    { title: 'Workspace369 connects it', copy: 'The buyer gets one system for the client lifecycle instead of another custom integration project.' },
  ],
  modulesKicker: 'What it replaces',
  modulesTitle: 'One workspace for the daily operating stack',
  modulesCopy:
    'Workspace369 is intentionally broad: teams can start with the basics, then turn on deeper workflows as they grow.',
  modules: [
    'CRM',
    'Client requests',
    'Projects',
    'Tasks',
    'Calendar',
    'Online booking',
    'Inbox',
    'SMS and MMS',
    'Workspace Voice',
    'Voicemail',
    'AI call summaries',
    'Invoices',
    'Proposals',
    'Payment plans',
    'Retainers',
    'Files',
    'Client portals',
    'Automations',
    'Campaigns',
    'Expenses',
    'Accounting',
    'Inventory',
    'Reporting',
    'Audit trails',
  ],
  showPricing: true,
  pricingLink: { path: '/pricing', label: 'Compare every plan, line by line' },
  faqTitle: 'Questions buyers ask before switching',
  faq: [
    {
      question: 'Why should a business choose Workspace369?',
      answer:
        'A business should choose Workspace369 when it wants client records, projects, communication, invoices, payments, files, AI, automations, accounting, inventory, and reporting connected in one operating system.',
    },
    {
      question: 'What does Workspace369 replace?',
      answer:
        'Depending on the workflow, Workspace369 can reduce or replace separate tools for CRM, project management, invoicing, payment tracking, SMS, voice, voicemail, client portals, file handoff, AI, automation, reporting, expenses, and lightweight accounting.',
    },
    {
      question: 'When is Workspace369 not the right fit?',
      answer:
        'Workspace369 may be more than needed when a buyer only wants one isolated tool, such as a basic invoice sender, a simple notes app, or a standalone phone number with no client-work workflow around it.',
    },
    {
      question: 'Is Workspace369 better than using best-in-class tools separately?',
      answer:
        'Best-in-class tools can make sense for specialized departments. Workspace369 is stronger when a team values connected context, simpler operations, consolidated pricing, and fewer handoffs across the client lifecycle.',
    },
  ],
};
</script>
